import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Button,
    Container,
    ThemeProvider,
    createTheme,
    CssBaseline,
    Stack,
    useMediaQuery,
    Slide,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { PhoneAndroid, Email } from "@mui/icons-material";
import easyKnowLogo from "../../assests/logo.png"; // Adjust the path as necessary
import { getUserId } from "../../utils/cookieUtils";

const Transition = React.forwardRef((props, ref) => (
    <Slide direction="down" ref={ref} {...props} />
));

const theme = createTheme({
    palette: {
        primary: {
            main: "hsl(213, 100%, 50%)",
            light: "hsl(213, 100%, 65%)",
        },
        secondary: {
            main: "hsl(285, 85%, 58%)",
            light: "hsl(285, 85%, 70%)",
        },
        background: {
            default: "hsl(220, 25%, 97%)",
            paper: "#ffffff",
        },
        text: {
            primary: "hsl(220, 25%, 15%)",
            secondary: "hsl(220, 15%, 55%)",
        },
    },
    Typography: {
        fontFamily:
            '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        h3: {
            fontWeight: 700,
            fontSize: "2.5rem",
            letterSpacing: "-0.02em",
        },
        body1: {
            fontSize: "1.1rem",
            lineHeight: 1.6,
        },
    },
    shape: {
        borderRadius: 12,
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "1rem",
                    padding: "14px 32px",
                    borderRadius: "12px",
                    boxShadow: "none",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                        boxShadow: "0 8px 25px -8px rgba(0, 0, 0, 0.15)",
                        transform: "translateY(-2px)",
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    boxShadow: "0 8px 30px -5px rgba(0, 0, 0, 0.08)",
                    border: "1px solid hsl(220, 15%, 92%)",
                    backdropFilter: "blur(10px)",
                },
            },
        },
    },
});
const LoginPage = () => {
    const navigate = useNavigate();
    const [installPrompt, setInstallPrompt] = useState(null);
    const [showInstallModal, setShowInstallModal] = useState(false);

    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

    // Check if user previously cancelled the install prompt
    const hasUserCancelledInstall = () => {
        return localStorage.getItem("installPromptCancelled") === "true";
    };

    // Check if app is already installed
    const isAppInstalled = () => {
        // Check if running in standalone mode (installed PWA)
        if (window.matchMedia("(display-mode: standalone)").matches) {
            return true;
        }
        // Check if running in fullscreen mode (iOS)
        if (window.navigator.standalone === true) {
            return true;
        }
        return false;
    };

    useEffect(() => {
        // ⭐ Don't redirect if already on dashboard route (let Dashboard handle URL params)
        if (window.location.pathname.startsWith('/dashboard')) {
            return;
        }
        
        // ⭐ Check for URL params first (switching from admin)
        const urlParams = new URLSearchParams(window.location.search);
        const userIdParam = urlParams.get('user_id');
        const companyIdParam = urlParams.get('company_id');
        const tokenParam = urlParams.get('token');
        
        if (userIdParam) {
            // Forward ALL switch params (token is required for Sanctum)
            const params = new URLSearchParams();
            params.set('user_id', userIdParam);
            if (companyIdParam) {
                params.set('company_id', companyIdParam);
            }
            if (tokenParam) {
                params.set('token', tokenParam);
            }
            navigate(`/dashboard/home?${params.toString()}`, { replace: true });
            return;
        }
        
        // Otherwise, check cookie
        const userId = getUserId();
        if (userId) {
            navigate("/dashboard");
            return;
        }

        // Don't show popup if app is already installed
        if (isAppInstalled()) {
            return;
        }

        const isMobile =
            /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
            navigator.maxTouchPoints > 0;
        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
        const isChrome =
            /Chrome/.test(navigator.userAgent) &&
            /CriOS/.test(navigator.userAgent); // Detect Chrome on iOS

        const handleBeforeInstallPrompt = (event) => {
            if (!isMobile || isIOS) return; // ✅ Prevent on desktop & iOS

            // Don't show if user previously cancelled
            if (hasUserCancelledInstall()) {
                return;
            }

            event.preventDefault();
            setInstallPrompt(event);
            setShowInstallModal(true);
        };

        window.addEventListener(
            "beforeinstallprompt",
            handleBeforeInstallPrompt
        );

        // ✅ If iOS, show the manual installation guide (only if not cancelled)
        if (isIOS && !hasUserCancelledInstall()) {
            setShowInstallModal(true);
        }

        // Listen for app installation event
        const handleAppInstalled = () => {
            // Clear any cancellation flag since app is now installed
            localStorage.removeItem("installPromptCancelled");
            setShowInstallModal(false);
        };

        window.addEventListener("appinstalled", handleAppInstalled);

        return () => {
            window.removeEventListener(
                "beforeinstallprompt",
                handleBeforeInstallPrompt
            );
            window.removeEventListener("appinstalled", handleAppInstalled);
        };
    }, [navigate]);

    const handleInstallClick = () => {
        if (installPrompt) {
            installPrompt.prompt();
            installPrompt.userChoice.then((choiceResult) => {
                setShowInstallModal(false);
                // If user installs, clear any cancellation flag
                if (choiceResult.outcome === "accepted") {
                    localStorage.removeItem("installPromptCancelled");
                }
            });
        }
    };

    const handleCloseModal = () => {
        // Store cancellation decision in localStorage
        localStorage.setItem("installPromptCancelled", "true");
        setShowInstallModal(false);
    };

    const sanitizeEnv = (value) => value?.replace(/\s+#.*$/, "").trim();

    // Local-only login: SecureIVS rejects localhost redirect_uri, so skip the hub.
    // Only active under `npm start` (NODE_ENV=development) on localhost with REACT_APP_LOCAL_LOGIN=true.
    const isLocalLogin =
        process.env.NODE_ENV === "development" &&
        sanitizeEnv(process.env.REACT_APP_LOCAL_LOGIN) === "true" &&
        ["localhost", "127.0.0.1"].includes(window.location.hostname);

    const redirectToLoginHub = (loginMethod) => {
        if (isLocalLogin) {
            const user = window.prompt(
                `Local login (${loginMethod === "sms" ? "mobile number" : "email"}):`,
                sanitizeEnv(process.env.REACT_APP_LOCAL_LOGIN_USER) || ""
            );
            if (user && user.trim()) {
                navigate(`/verify-mobile?user=${encodeURIComponent(user.trim())}`);
            }
            return;
        }

        const baseURL = sanitizeEnv(process.env.REACT_APP_CUSTOMER_API_URL);
        const clientId = sanitizeEnv(process.env.REACT_APP_CLIENT_ID);
        const appUrl = sanitizeEnv(process.env.REACT_APP_APP_URL || window.location.origin)?.replace(/\/$/, "");

        if (!baseURL || !clientId) {
            console.error("Missing REACT_APP_CUSTOMER_API_URL or REACT_APP_CLIENT_ID in front-end/.env");
            return;
        }

        const redirectURI = `${appUrl}/verify-mobile`;
        const loginURL = `${baseURL}?client_id=${encodeURIComponent(clientId)}&login_method=${loginMethod}&redirect_uri=${encodeURIComponent(
            redirectURI
        )}`;
        window.location.href = loginURL;
    };

    const handleLogin = () => redirectToLoginHub("sms");

    const handleLoginEmail = () => redirectToLoginHub("email");

    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <Box
            sx={{
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                bgcolor: "#f5f5f5",
                px: 2,
            }}
        >
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <Box
                    sx={{
                        minHeight: "100vh",
                        background:
                            "linear-gradient(135deg, hsl(220, 25%, 97%), hsl(220, 15%, 95%))",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 2,
                        position: "relative",
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background:
                                "radial-gradient(ellipse 800px 600px at 50% 0%, hsl(213, 100%, 96%), transparent)",
                            pointerEvents: "none",
                        },
                    }}
                >
                    {/* Login Box */}
                    <Container maxWidth="sm">
                        <Card
                            sx={{
                                maxWidth: 450,
                                mx: "auto",
                                position: "relative",
                                overflow: "visible",
                            }}
                        >
                            <CardContent
                                sx={{
                                    p: { xs: 3, sm: 5 },
                                    textAlign: "center",
                                }}
                            >
                                {/* Logo */}
                                <Box
                                    sx={{
                                        mb: 3,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        gap: 2,
                                    }}
                                >
                                    <Box
                                        component="img"
                                         src={easyKnowLogo}
                                        alt="easyKNOW Logo"
                                        sx={{
                                            width: 88,
                                            height: 88,
                                           // borderRadius: "12px",
                                           // background: "linear-gradient(135deg, hsl(213, 100%, 50%), hsl(285, 85%, 58%))",
                                            padding: "4px",
                                            objectFit: "contain",
                                        }}
                                    />
                                    <Typography
                                        variant="h3"
                                        sx={{
                                            background: '#3f72af',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            backgroundClip: 'text',
                                            fontSize: isMobile ? '2rem' : '3.0rem',
                                            fontWeight: 600,
                                        }}
                                    >
                                        easyKNOW
                                    </Typography>
                                </Box>

                                {/* Welcome Message */}
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{
                                        mb: 4,
                                        fontWeight: 400,
                                    }}
                                >
                                    Welcome back! Please login to continue.
                                </Typography>

                                {/* Sign In Buttons */}
                                <Stack spacing={2.5}>
                                    {/* <Button
                                        variant="contained"
                                        size="large"
                                        startIcon={<PhoneAndroid />}
                                        onClick={handleLogin}
                                        sx={{
                                            background:
                                                "#3f72af",
                                            color: "white",
                                            "&:hover": {
                                                background:
                                                    "linear-gradient(135deg, hsl(213, 100%, 45%), hsl(213, 100%, 60%))",
                                            },
                                        }}
                                        fullWidth
                                    >
                                        Sign in with Mobile
                                    </Button>  */}

                                    <Button
                                        variant="contained"
                                        size="large"
                                        startIcon={<Email />}
                                        onClick={handleLoginEmail}
                                        sx={{
                                            background:
                                                "#3f72af",
                                            color: "white",
                                            "&:hover": {
                                                background:
                                                    "linear-gradient(135deg, hsl(213, 100%, 45%), hsl(213, 100%, 60%))",
                                            },
                                        }}
                                        fullWidth
                                    >
                                        Sign in with Email
                                    </Button>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Container>
                </Box>
            </ThemeProvider>

            {/* ✅ Install App Modal */}
            <Dialog
                open={showInstallModal}
                onClose={handleCloseModal}
                TransitionComponent={Transition}
                sx={{
                    "& .MuiDialog-container": {
                        alignItems: "center",
                    },
                    "& .MuiPaper-root": {
                        position: "absolute",
                        top: "8%",
                        width: "80%",
                        borderRadius: "8px",
                    },
                }}
            >
                <DialogTitle>Install easyKNOW App</DialogTitle>
                <DialogContent>
                    {isIOS ? (
                        <Typography variant="body1">
                            To install easyKNOW on iOS, open{" "}
                            <strong>Safari</strong>, tap the{" "}
                            <strong>Share</strong> button, then select{" "}
                            <strong>"Add to Home Screen"</strong>.
                        </Typography>
                    ) : (
                        <Typography variant="body1">
                            Install this app on your device for a better
                            experience.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal} color="secondary">
                        Close
                    </Button>
                    {!isIOS && (
                        <Button
                            onClick={handleInstallClick}
                            color="primary"
                            variant="contained"
                        >
                            Install
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LoginPage;
